import React, { useCallback, useEffect, useState } from "react";
import LiveModal from "./LiveModal.tsx";
import { Call, useStreamVideoClient } from "@stream-io/video-react-sdk";
import { useNavigate } from "react-router-dom";
import Toast from "../Sweetalert/Sweetalert.js";
import axios from "axios";
import { API } from "../../shared/api/Api.js";
import { useDispatch, useSelector } from "react-redux";
import { currentLive } from "../../features/live/liveSlice.js";
import { selectUserInformations } from "../../features/authSlice.js";
// @ts-ignore
import LiveStart from "../../assets/gif/live_started.gif";
import { hasRole, showToast } from "../../utils/utils.js";

const initialValues = {
  dateTime: new Date(),
  description: "",
  link: "",
};

const LiveTypeList = ({
  liveState,
  setLiveState,
  lesson,
}: {
  liveState: any;
  setLiveState: any;
  lesson: any;
}) => {
  const [values, setValues] = useState(initialValues);
  const [callDetail, setCallDetail] = useState<Call>();
  const user = useSelector(selectUserInformations);
  const client = useStreamVideoClient();
  const [loader, setLoader] = useState<boolean>(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const createLive = useCallback(async () => {
    if (!client || !user) {
      showToast("Error", "Missing required credentials", "error");
      return;
    }
    if (!(user.instructor || hasRole(user.roles, "super-admin"))) {
      showToast("Error", "Insufficient permissions", "error");
      return;
    }
    const MAX_RETRIES = 3;
    let retries = 0;
    let call: Call | undefined;
    setLoader(true);

    try {
      const id = lesson.room.uuid;
      const startsAt =
        values.dateTime.toISOString() || new Date().toISOString();
      const description = values.description || "Instant Live";

      while (retries < MAX_RETRIES) {
        try {
          call = client.call("default", id);
          if (!call) {
            throw new Error("Failed to initialize call");
          }

          await call.getOrCreate({
            data: {
              starts_at: startsAt,
              custom: {
                description,
              },
            },
          });
          break; // Exit loop if successful
        } catch (error: any) {
          retries++;

          // Check if it's a timeout error
          if (error.code === "ECONNABORTED" || error.name === "AxiosError") {
            if (retries < MAX_RETRIES) {
              // Show retry message
              Toast.fire({
                text: `Poor connection. Retrying (${retries}/${MAX_RETRIES})...`,
                icon: "info",
                timer: 2000,
              });

              // Wait 2 seconds before retrying
              await new Promise((resolve) => setTimeout(resolve, 2000));
              continue;
            }
          }

          // Throw other errors or final timeout error
          throw error;
        }
      }

      if (!call) {
        throw new Error("Call creation failed");
      }

      setCallDetail(call);

      if (!values.description) {
        await handleLiveCreation(call, lesson);
        navigate(`/dashboard/courses/lesson/live/${id}`, {
          state: { lesson_id: lesson.id },
        });
      }
    } catch (error) {
      console.error(error);

      // Show appropriate error message
      const errorMessage =
        retries >= MAX_RETRIES
          ? "Failed to start live after multiple attempts. Please check your internet connection."
          : "An error occurred while starting the live session.";

      Toast.fire({
        text: errorMessage,
        icon: "error",
      });
    } finally {
      setLoader(false);
    }
  }, [client, user, lesson]);

  const handleLiveCreation = useCallback(
    async (call: Call, lesson: any) => {
      const { data } = await axios.post(
        API.Courses.app_courses_live_startLive,
        {
          lesson_id: lesson.id,
          call_cid: call.cid,
          note: "Start Live",
        },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      if (data.status === 201 && data.errorCode === 0) {
        dispatch(currentLive(data.data));
      }
    },
    [callDetail]
  );

  // useEffect(() => {
  //   if (!lesson?.room?.uuid) {
  //     showToast("Error", "Invalid lesson configuration", "error");
  //     setLiveState(undefined);
  //   }
  // }, [lesson]);

  return (
    <div
      style={{
        grid: "initial",
        gridColumn: 1,
        gap: 5,
      }}
    >
      <LiveModal
        isOpen={liveState === "isInstantLive"}
        onClose={() => setLiveState(undefined)}
        title="Start an Instant Live"
        className="text-center"
        buttonText="Start Live"
        handleClick={createLive}
        loader={loader}
        image={LiveStart}
      />
    </div>
  );
};

export default LiveTypeList;
